import { Accordion, Avatar, Group, Stack, Text } from '@mantine/core';
import { primaryColor } from '../../../../constants/theme';
import { getProfilePicture } from '../../../../types/UserDocument';
import { useMemo, useState } from 'react';
import { format } from 'date-fns';
import {
  IconArrowDownLeft,
  IconArrowUpRight,
  IconChevronDown,
} from '@tabler/icons-react';
import { ProfileButton } from './ProfileButton';
import { CancelBookingButton } from './CancelBookingButton';
import { BookingPaymentButton } from './BookingPaymentButton';
import { BookingResponseModel } from '../../../../types/BookingModels';
import { useApp } from '../../../../context/AppContext';
import { getBookingStatusBadge } from './BookingStatusBadge';
import { AcceptButton } from './AcceptButton';
import { DeclineBooking } from './DeclineBooking';
import { RemoveBookingButton } from './RemoveBookingButton';
import { JoinCallButton } from './JoinCallButton';
import { DisputeButtonBookingButton } from './NotHappyWithCall';
import { BookingNote } from './BookingNote';
import { AddToCalendarButton } from './AddToCalendarButton';
import { LeaveFeedbackButton } from './LeaveFeedbackButton';
import { getBookingTopicLabel } from '../../../../utils/bookingTopic';

export interface BookingItemNewProps {
  booking: BookingResponseModel;
  profile: any;
  groupedBookings: BookingResponseModel[];
  refreshBookings: () => void;
}

export const BookingItemNew: React.FC<BookingItemNewProps> = ({
  booking,
  profile,
  groupedBookings,
  refreshBookings,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const bookingType = useMemo(
    () => (booking.userId === profile.id ? 'outgoing' : 'incoming'),
    [booking.userId, profile.id]
  );

  return (
    <Accordion
      chevronPosition="right"
      variant="contained"
      radius="md"
      chevron={<></>}
      onChange={(value) => setIsExpanded(value !== null)}
    >
      <Accordion.Item value={booking.id?.toString()} key={booking.id}>
        <Accordion.Control
          aria-label={''}
          px={10}
          data-testid={`booking-accordion-control-${booking.id}`}
        >
          <AccordionLabel
            groupedBookings={groupedBookings}
            profile={profile}
            isExpanded={isExpanded}
          />
        </Accordion.Control>

        <Accordion.Panel>
          <AccordionContent
            bookingType={bookingType}
            booking={booking}
            refreshBookings={refreshBookings}
            groupedBookings={groupedBookings}
          />
        </Accordion.Panel>
      </Accordion.Item>
    </Accordion>
  );
};

export const AccordionLabel = ({
  groupedBookings,
  profile,
  hideChevron = false,
  isExpanded = false,
}: {
  groupedBookings: BookingResponseModel[];
  profile: any;
  hideChevron?: boolean;
  isExpanded?: boolean;
}) => {
  const booking = groupedBookings[0];
  const finalBooking = groupedBookings[groupedBookings.length - 1];
  const bookingType = useMemo(
    () => (booking.userId === profile.id ? 'outgoing' : 'incoming'),
    [booking.userId, profile.id]
  );

  // TODO here
  const profilePicture = useMemo(
    () =>
      bookingType === 'outgoing'
        ? getProfilePicture(booking.professionalProfilePicture)
        : getProfilePicture(booking.userProfilePicture),
    [
      bookingType,
      booking.professionalProfilePicture,
      booking.userProfilePicture,
    ]
  );

  const userName = useMemo(
    () =>
      bookingType === 'outgoing' ? booking.professionalName : booking.userName,
    [bookingType, booking.professionalName, booking.userName]
  );

  const formattedDate = useMemo(
    () => format(new Date(booking.startIso), 'EEE do MMM'),
    [booking.startIso]
  );

  const formattedTime = useMemo(() => {
    const start = new Date(booking.startIso);
    const end = new Date(finalBooking.endIso);
    return `${format(start, 'HH:mm')} - ${format(end, 'HH:mm')}`;
  }, [booking.startIso, finalBooking.endIso]);

  const bookingCountLabel = useMemo(() => {
    const count = groupedBookings.length;
    return `${count} booking${count === 1 ? '' : 's'}`;
  }, [groupedBookings.length]);

  const totalCost = useMemo(() => {
    return groupedBookings.reduce((acc, booking) => acc + booking.rate, 0);
  }, [groupedBookings]);

  const isPaid = useMemo(
    () => booking.paymentStatus === 'succeeded',
    [booking.paymentStatus]
  );

  const topicNames = useMemo(() => {
    return getBookingTopicLabel(booking);
  }, [booking]);

  return (
    <Stack gap={5}>
      <Group wrap="nowrap" w="100%" justify="space-between">
        <Group>
          <Avatar src={profilePicture} alt={userName} size={48} />
          <div>
            <Group gap={5}>
              <Text
                ta="left"
                fw={500}
                data-testid={`booking-title-text-${booking.id}`}
              >
                {userName}
              </Text>
              <Text c="dimmed">|</Text>
              <Text
                c="dimmed"
                td={isPaid ? 'line-through' : 'none'}
                data-testid={`booking-cost-text-${booking.id}`}
              >
                £{totalCost}
              </Text>
              {isPaid && (
                <Text
                  c="dimmed"
                  data-testid={`booking-paid-text-${booking.id}`}
                >
                  Paid
                </Text>
              )}
            </Group>
            <Group gap={5}>
              <Text c="dimmed" size="sm">
                {formattedDate}
              </Text>
              <Text c="dimmed" size="sm">
                {formattedTime}
              </Text>
            </Group>
            {topicNames && (
              <Text c="dimmed" size="sm" ta="start">
                Topic: {topicNames}
              </Text>
            )}
            <Text c="dimmed" size="sm" ta="start">
              {bookingCountLabel}
            </Text>
            {!hideChevron && (
              <Group gap={2} mt={2}>
                <Text size="xs" c="dimmed">
                  {isExpanded ? 'Hide details' : 'View details'}
                </Text>
                <IconChevronDown
                  size={13}
                  color="var(--mantine-color-dimmed)"
                  style={{
                    transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 200ms ease',
                  }}
                />
              </Group>
            )}
          </div>
        </Group>
        {bookingType === 'outgoing' ? (
          <Stack
            align="center"
            justify="center"
            gap={5}
            data-testid={`made-icon-${booking.id}`}
          >
            <IconArrowUpRight size={30} color={primaryColor} />
            <Text c="dimmed" size="xs" ta="center">
              Outgoing
            </Text>
          </Stack>
        ) : (
          <Stack
            align="center"
            justify="center"
            gap={5}
            data-testid={`received-icon-${booking.id}`}
          >
            <IconArrowDownLeft size={30} color={primaryColor} />
            <Text c="dimmed" size="xs" ta="center">
              Incoming
            </Text>
          </Stack>
        )}
      </Group>
      <Group justify="end">{getBookingStatusBadge(booking)}</Group>
    </Stack>
  );
};

export interface AccordionContentProps {
  bookingType: string;
  booking: BookingResponseModel;
  refreshBookings: () => void;
  groupedBookings: BookingResponseModel[];
}

export const AccordionContent = ({
  bookingType,
  booking,
  refreshBookings,
  groupedBookings,
}: AccordionContentProps) => {
  const { profile, token } = useApp();
  const isExpired = useMemo(
    () => new Date(booking.startIso) <= new Date(),
    [booking.startIso]
  );
  const hasNoPayment = useMemo(
    () =>
      booking.accepted &&
      booking.paymentStatus !== 'succeeded' &&
      new Date(booking.endIso) < new Date() &&
      !booking.isCancelled,
    [
      booking.accepted,
      booking.endIso,
      booking.isCancelled,
      booking.paymentStatus,
    ]
  );
  const showNoResponseNote = useMemo(
    () =>
      bookingType === 'outgoing' &&
      !booking.accepted &&
      !booking.rejected &&
      isExpired &&
      !booking.isCancelled,
    [
      bookingType,
      booking.accepted,
      booking.isCancelled,
      booking.rejected,
      isExpired,
    ]
  );

  return (
    <Stack gap={10}>
      <Group gap={5} mt="xs" align="cente">
        <ProfileButton
          userId={
            booking.userId === profile?.id
              ? booking.professionalId
              : booking.userId
          }
          width={105}
          bookingId={booking.id}
        />
        <CancelBookingButton
          refreshBookings={() => refreshBookings()}
          profileId={profile?.id || ''}
          groupedBookings={groupedBookings}
        />
        <BookingPaymentButton
          booking={booking}
          profileId={profile?.id || ''}
          groupedBookings={groupedBookings}
        />
        <AcceptButton
          groupedBookings={groupedBookings}
          profileId={profile?.id || ''}
          token={token}
          refreshBookings={() => refreshBookings()}
        />
        <DeclineBooking
          groupedBookings={groupedBookings}
          profileId={profile?.id || ''}
          refreshBookings={() => refreshBookings()}
        />
        <RemoveBookingButton
          refreshBookings={() => refreshBookings()}
          profileId={profile?.id || ''}
          groupedBookings={groupedBookings}
        />
        <JoinCallButton
          groupedBookings={groupedBookings}
        />
      </Group>
      <DisputeButtonBookingButton
        refreshBookings={() => refreshBookings()}
        profileId={profile?.id || ''}
        groupedBookings={groupedBookings}
      />
      <LeaveFeedbackButton
        groupedBookings={groupedBookings}
        profileId={profile?.id || ''}
        token={token || ''}
        refreshBookings={() => refreshBookings()}
      />
      {booking.isCancelled && booking.cancellationReason ? (
        <BookingNote variant="cancellation" text={booking.cancellationReason} />
      ) : hasNoPayment ? (
        <BookingNote variant="no-payment" />
      ) : showNoResponseNote ? (
        <BookingNote variant="no-response" />
      ) : booking.message ? (
        <BookingNote variant="comment" text={booking.message} />
      ) : null}
    </Stack>
  );
};
